import React, { useEffect, useState } from 'react';
import { NextSeo } from 'next-seo';
import { useUserAgent } from 'next-useragent';
import nookies, { setCookie } from 'nookies';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import { hasSeo, seoData } from '../../../utils/seo';
import { SearchStoreProvider } from '../../../providers/SearchStoreProvider';
import { RootStoreProvider } from '../../../providers/RootStoreProvider';
import { Layout } from '../../../components/Layout';
import { SearchPage } from '../../../components/SearchPage';
import { Collapsed } from '../../../components/Collapsed';
import styles from '../../../styles/result.module.sass';
import { BreadCrumbs } from '../../../components/BreadCrumbs';
import { apiProxy } from '../../../lib/api';
import { ModalReload } from '../../../components/ModalReload';
import { yandexUserLayer } from '../../../lib/yandexDataLayer';
import { useHiddenWithCookie } from '../../../hooks/useHiddenWithCookie';
import { YaAndGaPopdirPage } from '../../../lib/YaAndGaHelper';
import { redirectFromTo } from '../../../utils/redirectFromTo';
import { logger } from '../../../lib/logger';

const BannerTop = dynamic(() =>
  import('../../../components/CookieBanner').then(mod => mod.CookieBanner),
);

export default function ResultPage({
  uaString,
  store: {
    user,
    popdir,
    url,
    cruises,
    slidersBanner,
    seoRaw,
    banners,
    isSea,
    cookies,
    popularPopdirs,
  },
  canonicalUrl,
}) {
  const ua = useUserAgent(uaString || window.navigator.userAgent);
  const { isMobile, isTablet } = ua;
  const pageRoute = [
    { title: 'Популярные направления', link: 'https://kruiz.online/popdir' },
    { title: popdir?.caption, link: '' },
  ];

  useEffect(() => {
    // Яндекс метрика - информация о пользователи и типе страницы
    yandexUserLayer(user?.user?.id, user?.user?.group, isSea);
    // YaAndGaPopdirPage(isSea ? 'isSeaPage' : 'isRiverPage');
  }, [isSea]);

  const replaceCaption =
    popdir?.caption.split(' ')[0] === 'Круизы' ? 'Круизы' : 'Речные круизы';

  const dynamicPopdirTitle = type => {
    switch (type) {
      case 1:
      case 2:
      case 3:
      case 4:
        return `${popdir?.caption} на теплоходе в 2025 году – расписание и цены`;
      case 10:
        return `Речные круизы из ${popdir?.caption.split(' ')[2]} на ${
          popdir?.caption.split(' ')[4]
        } дней на теплоходе в 2025 году – расписание и цены`;
      case 7:
        return `Речные круизы из ${popdir?.caption.split(' ')[2]} в ${
          popdir?.caption.split(' ')[4]
        } 2025 года на теплоходе – расписание и цены`;
      case 8:
      case 11:
        return `Речные круизы на теплоходе${popdir?.caption.replace(
          replaceCaption,
          '',
        )} в 2025 году - расписание и цены`;
      default:
        return '';
    }
  };

  const dynamicPopdirDescription = type => {
    switch (type) {
      case 1:
      case 2:
      case 3:
      case 4:
      case 8:
      case 11:
        return `${popdir?.caption} на теплоходе. 🚢 Онлайн-бронирование речных туров. Расписание на 2025 год. Описание маршрутов и экскурсий, цены, выбор каюты. +7 499 643-57-40`;
      case 10:
        return `Круизы из ${popdir?.caption.split(' ')[2]} на ${
          popdir?.caption.split(' ')[4]
        } дней на теплоходе. 🚢 Онлайн-бронирование речных туров. Расписание на 2025 год. Описание маршрутов и экскурсий, цены, выбор каюты. +7 499 643-57-40`;
      case 7:
        return `Круизы из ${popdir?.caption.split(' ')[2]} в ${
          popdir?.caption.split(' ')[4]
        } на теплоходе. 🚢 Онлайн-бронирование речных туров. Расписание на 2025 год. Описание маршрутов и экскурсий, цены, выбор каюты. +7 499 643-57-40`;
      default:
        return '';
    }
  };
  const [closeBanners, setCloseBanners] = useState(
    cookies?.closeBanner !== undefined || cookies?.closeBanner === 'false',
  );
  const currentDate = dayjs();
  const timeToCookie = currentDate.add(2, 'h');
  setCookie(null, 'closeBanner', 'false');
  const openBanner = () => {
    setCloseBanners(false);
    setCookie(null, 'closeBanner', 'false');
  };
  useHiddenWithCookie(openBanner);

  return (
    <SearchStoreProvider
      hydrationData={{ user, popdir, url, searchStore: cruises, isSea }}
    >
      <RootStoreProvider
        hydrationData={{ user, popdir, url, searchStore: cruises, isSea }}
      >
        <NextSeo
          title={
            popdir?.seo_title.length !== 0
              ? popdir?.seo_title
              : dynamicPopdirTitle(popdir?.type)
          }
          description={
            popdir?.seo_description.length !== 0
              ? popdir?.seo_description
              : dynamicPopdirDescription(popdir?.type)
          }
          additionalLinkTags={[
            {
              rel: 'shortcut icon',
              href: '/favicon.ico?v=2',
            },
          ]}
          canonical={canonicalUrl}
        />
        <Layout
          isMobile={isMobile}
          isTablet={isTablet}
          isSea={isSea}
          showTopContent={!closeBanners}
          popularPopdirs={popularPopdirs}
          topContent={
            <div>
              <BannerTop
                isOpen={closeBanners}
                onOpen={setCloseBanners}
                timeToCookie={timeToCookie}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </div>
          }
        >
          <div className="container--block">
            <div
              className={!closeBanners ? styles.crumbsExtended : styles.crumbs}
            >
              <BreadCrumbs routeCrumbs={pageRoute} />
            </div>
            <SearchPage
              noRedirectToResult
              url={url}
              direction={`direction=${url}`}
              popdir={popdir}
              isMobile={isMobile}
              description={popdir?.popdir_description}
              slidersBanner={slidersBanner}
              urlBanners={banners}
              isSea={isSea}
            />
            <section className={styles.faq}>
              {/* <h2 className="visually-hidden"> Часто задаваемые вопросы </h2> */}
              {hasSeo(seoRaw, popdir) && (
                <Collapsed seo={seoData(seoRaw)} shorter isSeo />
              )}
            </section>
          </div>
        </Layout>
        <ModalReload />
      </RootStoreProvider>
    </SearchStoreProvider>
  );
}

export const getServerSideProps = async ctx => {
  const cookies = nookies.get(ctx);
  const { token } = cookies;

  // энкодим русские буквы для корректной работы ручки
  const encodedId = encodeURI(ctx.query.id);
  // Получаем только путь без query параметров
  const canonicalPath = ctx.req.url.split('?')[0];
  const canonicalUrl = `https://kruiz.online${canonicalPath}`;

  const popdirRaw = await Promise.allSettled([
    apiProxy.get(`ApiPopdir/popdir_seo_result/${encodedId}`, {
      headers: {
        'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
        ym_uid: cookies._ym_uid || 'ym_cid not found',
      },
    }),
  ]);

  const [popdir] = popdirRaw.map(req =>
    req.status === 'fulfilled' ? req.value.data ?? null : null,
  );

  if (popdir?.redirect.code !== 200) {
    return redirectFromTo(popdir?.redirect, ctx);
  }

  try {
    const isSea = popdir.direction === 'sea' ?? false;

    const responses = await Promise.allSettled([
      apiProxy.get(`ApiUsers/check_token/${token ?? ''}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
      apiProxy.get(
        `Booking2019/get_cruis_list/${isSea ? 'sea' : 'new_river'}`,
        {
          headers: {
            'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
            ym_uid: cookies._ym_uid || 'ym_cid not found',
          },
        },
      ),
      apiProxy.get(`ApiPopdir/get_popdir?href=${ctx.query.id}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
      apiProxy.get(`ApiBanners/getResultBanners`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
      apiProxy.get(`ApiPopdirNew/getHeaderPopdir/${isSea ? 1 : 0}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
    ]);

    const [user, cruises, seoRaw, banners, popularPopdirs] = responses.map(
      req => (req.status === 'fulfilled' ? req.value.data ?? null : null),
    );

    const sliders = await Promise.allSettled([
      apiProxy.get(`ApiPopdir/getPopdirSlider/${popdir.filter_slider}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
    ]);

    const [slidersBanner] = sliders.map(req =>
      req.status === 'fulfilled' ? req.value.data ?? null : null,
    );
    if (isSea) {
      return {
        redirect: {
          destination: `/popdir/${ctx.query.id}`,
          permanent: true,
        },
      };
    }
    logger({
      additionalFields: {
        url: ctx.resolvedUrl,
      },
      level: 'debug',
      ym_uid: cookies._ym_uid,
      message: 'page has been created',
    });
    return {
      props: {
        store: {
          user,
          popdir,
          url: ctx.query.id,
          cruises,
          slidersBanner,
          seoRaw,
          banners,
          isSea,
          cookies,
          popularPopdirs,
        },
        uaString: ctx.req.headers['user-agent'],
        canonicalUrl,
      },
    };
  } catch (error) {
    logger({
      additionalFields: {
        url: ctx.resolvedUrl,
      },
      error,
      level: 'error',
      message: 'page was not created',
    });
    return redirectFromTo({ url: '', code: 503 }, ctx);
  }
};
